import React, { useState, useEffect } from "react";
import "../Assets/css/login.css"; // external CSS file
import axiosUserInstance from "../axiosUserInstance";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Button } from "react-bootstrap";
import { GoogleLogin } from "@react-oauth/google";
import BgVideo from "../Assets/video/login-signup-bg.mp4";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [muted, setMuted] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // 🔹 Validate email with regex
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");

    let valid = true;

    // 🔹 Email validation
    if (!email) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email.");
      valid = false;
    }

    // 🔹 Password validation
    if (!password) {
      setPasswordError("Password is required.");
      valid = false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      valid = false;
    }

    if (!valid) return; // Stop if validation fails

    setIsLoading(true);

    try {
      const response = await axiosUserInstance.post(`/user/loginUser`, {
        emailOrUsername: email,
        password,
      });
      if (response?.status === 200) {
        const data = await response.data;
        localStorage.setItem("token", data.token);
        const decoded = jwtDecode(data.token);
        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            userId: decoded?.userId,
            userName: decoded?.userName,
            email: decoded?.email,
          })
        );
        if (decoded?.roleId === "user") {
          navigate("/user/dashboard");
        } else if (decoded?.roleId === "admin") {
          navigate("/admin/dashboard");
        }
        toast.success("Login successfully.");
      } else {
        toast.error(response?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsLoading(true);
      const response = await axiosUserInstance.post(`/user/loginUser`, {
        mode: "googleLogin",
        googleToken: credentialResponse.credential,
      });

      if (response?.status === 200 || response?.status === 201) {
        const data = await response.data;
        localStorage.setItem("token", data.token);
        const decoded = jwtDecode(data.token);
        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            userId: decoded?.userId,
            userName: decoded?.userName,
            email: decoded?.email,
          })
        );
        if (decoded?.roleId === "user") {
          navigate("/user/dashboard");
        } else if (decoded?.roleId === "admin") {
          navigate("/admin/dashboard");
        }
        toast.success("Login successfully.");
      } else {
        toast.error(response?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "Google login failed. Please try again.";
      toast.error(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error("Google login failed. Please try again.");
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      if (decoded?.roleId === "user") {
        navigate("/user/dashboard");
      } else if (decoded?.roleId === "admin") {
        navigate("/admin/dashboard");
      }
    }
  }, []);

  return (
    <div className="login-page">
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted={muted}
        playsInline
        className="video-background"
        id="bgVideo"
      >
        <source src={BgVideo} type="video/mp4" />
      </video>

      {/* Toggle Button */}
      <button className="audio-toggle" onClick={() => setMuted(!muted)}>
        <i className={`fas ${muted ? "fa-volume-mute" : "fa-volume-up"}`}></i>
      </button>

      {/* Login Box */}
      <div className="login-container">
        <div className="login-header">
          <h1>BioME.com</h1>
          <p>All of you, in 1 #BioME.com!</p>
        </div>

        <div className="login-form">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailError && <div className="error-message">{emailError}</div>}
            </div>

            <div className="form-group">
              <label>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: "#666",
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <AiOutlineEyeInvisible size={20} />
                  ) : (
                    <AiOutlineEye size={20} />
                  )}
                </button>
              </div>
              {passwordError && (
                <div className="error-message">{passwordError}</div>
              )}
            </div>

            <Button
              className="btn btn-primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Loading…" : "Login"}
            </Button>

            <div className="divider">or</div>

            <div style={{ display: "flex", justifyContent: "center", marginTop: "15px" }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>

            <div className="login-footer">
              Don't have an account? <Link to={"/auth/signup"}>Sign up</Link>
              <br />
              <Link to="/auth/forgot-password">Forgot password?</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
